import { defineStore } from 'pinia'
import DsPolicyService from '@/Network/Services/DsPolicyService'
import {
  DsPolicyItem,
  ListDsPolicyResponse,
  AuthorizedDoctor,
  BlockedDoctor,
  AuthorizedDoctorDsPolicyResponse,
  BlockedDoctorDsPolicyResponse,
  DsPolicyDetails,
  DsPolicyPatchDetails,
  GetCompanyDomainResponse,
  GetHealthUnitDomainResponse,
  GetPositionDomainResponse,
  GetScheduleDomainResponse
} from '@/Network/Types/Responses/DsPolicyResponses'
import {
  CreateDsPolicyParams,
  DsPolicySearchParams,
  UpdateDsPolicyParams
} from '@/Network/Types/Requests/DsPolicyRequests'
import { getApiErrorMessage } from '@/utils/apiError'

export const DS_POLICIES_LOAD_ERROR = 'Não foi possível carregar as políticas de restrição.'

/**
 * Contrato de erro (rule `error-handling.md`):
 * - `fetchDsPolicies` é o load da listagem: NÃO relança (tem callers fire-and-forget —
 *   `changePage`, `clearFilters`, `onMounted` da listagem e o refresh interno do
 *   `duplicateDsPolicy`) e expõe `loadError` + `hasError` para a view desenhar o estado de erro.
 * - Todas as demais actions relançam: o `ServiceError` do `DsPolicyService` chega intacto ao
 *   `notifyError` do componente, que decide o fallback específico da operação.
 */
interface PolicyState {
  policies: DsPolicyItem[]
  policyQuery: DsPolicySearchParams
  totalPolicies: number
  isLoading: boolean
  loadError: string | null
  page: number
  currentPolicy: DsPolicyDetails | null

  authorizedDoctors: AuthorizedDoctor[]
  blockedDoctors: BlockedDoctor[]
  totalAuthorized: number
  totalBlocked: number
  isDoctorsLoading: boolean
}

export const useDsPolicyStore = defineStore('policy', {
  state: (): PolicyState => ({
    policies: [],
    policyQuery: {
      q: '',
      limit: 10,
      offset: 0,
      companyIds: ''
    },
    totalPolicies: 0,
    isLoading: false,
    loadError: null,
    page: 1,
    currentPolicy: null,

    authorizedDoctors: [],
    blockedDoctors: [],
    totalAuthorized: 0,
    totalBlocked: 0,
    isDoctorsLoading: false
  }),

  getters: {
    hasError: (state): boolean => state.loadError !== null
  },

  actions: {
    async fetchDsPolicies(): Promise<void> {
      const policyService = new DsPolicyService()
      try {
        this.loadError = null
        this.isLoading = true

        const { data, count }: ListDsPolicyResponse = await policyService.list(this.policyQuery)

        this.policies = data
        this.totalPolicies = count
      } catch (error) {
        console.error('Erro ao buscar políticas de restrição', error)
        this.policies = []
        this.totalPolicies = 0
        this.loadError = getApiErrorMessage(error, DS_POLICIES_LOAD_ERROR)
      } finally {
        this.isLoading = false
      }
    },

    async createDsPolicy(payload: CreateDsPolicyParams) {
      this.isLoading = true
      const service = new DsPolicyService()

      try {
        const policy = await service.create(payload)
        return policy
      } catch (error) {
        console.error('Erro ao criar política de restrição', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async fetchAuthorizedDoctors(dsPolicyId: number, params: DsPolicySearchParams): Promise<void> {
      const service = new DsPolicyService()
      this.isDoctorsLoading = true
      try {
        const { data, count }: AuthorizedDoctorDsPolicyResponse =
          await service.listAuthorizedDoctors(dsPolicyId, params || {})
        this.authorizedDoctors = data
        this.totalAuthorized = count
      } catch (error) {
        console.error('Erro ao buscar médicos autorizados', error)
        throw error
      } finally {
        this.isDoctorsLoading = false
      }
    },

    async fetchBlockedDoctors(dsPolicyId: number, params: DsPolicySearchParams): Promise<void> {
      const service = new DsPolicyService()
      this.isDoctorsLoading = true
      try {
        const { data, count }: BlockedDoctorDsPolicyResponse = await service.listBlockedDoctors(
          dsPolicyId,
          params
        )
        this.blockedDoctors = data
        this.totalBlocked = count
      } catch (error) {
        console.error('Erro ao buscar médicos bloqueados', error)
        throw error
      } finally {
        this.isDoctorsLoading = false
      }
    },

    async fetchDsPolicyById(id: number) {
      const service = new DsPolicyService()
      try {
        const response: DsPolicyDetails = await service.getById(id)
        this.currentPolicy = response
        localStorage.setItem('currentPolicyId', String(id))
        return response
      } catch (error) {
        this.clearCurrentDsPolicy()
        throw error
      }
    },

    clearCurrentDsPolicy() {
      this.currentPolicy = null
      localStorage.removeItem('currentPolicyId')
    },

    async clpatchDsPolicy(
      id: number,
      companyId: number,
      payload: UpdateDsPolicyParams
    ): Promise<DsPolicyPatchDetails> {
      const service = new DsPolicyService()
      this.isLoading = true
      try {
        const updated = await service.patch(id, companyId, payload)
        if (this.currentPolicy) {
          this.currentPolicy.name = updated.name
        }
        return updated
      } catch (error) {
        console.error('Erro ao atualizar política', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async disableDsPolicy(id: number): Promise<DsPolicyPatchDetails> {
      const service = new DsPolicyService()
      this.isLoading = true
      try {
        const disabledPolicy = await service.disableDsPolicy(id)

        const index = this.policies.findIndex((p) => p.id === id)
        if (index !== -1) {
          this.policies[index].isActive = false
        }

        return disabledPolicy
      } catch (error) {
        console.error('Erro ao desativar política', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async enableDsPolicy(id: number): Promise<DsPolicyPatchDetails> {
      const service = new DsPolicyService()
      this.isLoading = true
      try {
        const disabledPolicy = await service.enableDsPolicy(id)

        const index = this.policies.findIndex((p) => p.id === id)
        if (index !== -1) {
          this.policies[index].isActive = true
        }

        return disabledPolicy
      } catch (error) {
        console.error('Erro ao ativar política', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async duplicateDsPolicy(
      id: number,
      payload: { newPolicyName: string; targetCompanyId: number }
    ): Promise<DsPolicyItem> {
      const service = new DsPolicyService()
      this.isLoading = true
      try {
        const duplicated = await service.duplicateDsPolicy(id, payload)
        // Falha do refresh da listagem não relança (vira `loadError` na listagem): a
        // duplicação em si já foi concluída.
        await this.fetchDsPolicies()
        return duplicated
      } catch (error) {
        console.error('Erro ao duplicar política', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async addAuthorizedDoctors(dsPolicyId: number, data: any): Promise<void> {
      const service = new DsPolicyService()
      try {
        await service.addAuthorizedDoctors(dsPolicyId, data)
      } catch (error) {
        console.error('Erro ao adicionar médicos autorizados', error)
        throw error
      }
    },

    async addBlockedDoctors(
      dsPolicyId: number,
      payload: { dsPolicyId: number; doctorIds: number[] }
    ): Promise<void> {
      const service = new DsPolicyService()
      try {
        await service.addBlockedDoctors(dsPolicyId, payload)
      } catch (error) {
        console.error('Erro ao adicionar médicos bloqueados', error)
        throw error
      }
    },

    async deleteAuthorizedDoctor(dsPolicyAuthorizedId: number): Promise<void> {
      const service = new DsPolicyService()
      try {
        await service.deleteAuthorizedDoctor(dsPolicyAuthorizedId)

        this.authorizedDoctors = this.authorizedDoctors.filter(
          (doctor) => doctor.id !== dsPolicyAuthorizedId
        )
        this.totalAuthorized = this.authorizedDoctors.length
      } catch (error) {
        console.error('Erro ao remover médico autorizado:', error)
        throw error
      }
    },

    async deleteBlockedDoctor(dsPolicyBlockedId: number): Promise<void> {
      const service = new DsPolicyService()
      try {
        await service.deleteBlockedDoctor(dsPolicyBlockedId)
      } catch (error) {
        console.error('Erro ao remover médico bloqueado', error)
        throw error
      }
    },

    async fetchAuthorizedDoctorsByDsPolicy(dsPolicyId: number): Promise<void> {
      const service = new DsPolicyService()
      this.isDoctorsLoading = true
      try {
        const { data, count } = await service.listAuthorizedDoctorsByPolicy(dsPolicyId)
        this.authorizedDoctors = data
        this.totalAuthorized = count
      } catch (error) {
        console.error('Erro ao buscar médicos autorizados da política', error)
        throw error
      } finally {
        this.isDoctorsLoading = false
      }
    },

    async fetchBlockedDoctorsByDsPolicy(dsPolicyId: number): Promise<void> {
      const service = new DsPolicyService()
      this.isDoctorsLoading = true
      try {
        const { data, count } = await service.listBlockedDoctorsByPolicy(dsPolicyId)
        this.blockedDoctors = data
        this.totalBlocked = count
      } catch (error) {
        console.error('Erro ao buscar médicos bloqueados da política', error)
        throw error
      } finally {
        this.isDoctorsLoading = false
      }
    },

    async fetchCompanyDomain(dsPolicyId: number): Promise<GetCompanyDomainResponse> {
      const service = new DsPolicyService()
      try {
        return await service.getCompanyDomain(dsPolicyId)
      } catch (error) {
        console.error('Erro ao buscar domínio de empresas', error)
        throw error
      }
    },

    async fetchHealthUnitDomain(dsPolicyId: number): Promise<GetHealthUnitDomainResponse> {
      const service = new DsPolicyService()
      try {
        return await service.getHealthUnitDomain(dsPolicyId)
      } catch (error) {
        console.error('Erro ao buscar domínio de unidades de saúde', error)
        throw error
      }
    },

    async fetchPositionDomain(dsPolicyId: number): Promise<GetPositionDomainResponse> {
      const service = new DsPolicyService()
      try {
        return await service.getPositionDomain(dsPolicyId)
      } catch (error) {
        console.error('Erro ao buscar domínio de posições', error)
        throw error
      }
    },

    async fetchScheduleDomain(dsPolicyId: number): Promise<GetScheduleDomainResponse> {
      const service = new DsPolicyService()
      try {
        return await service.getScheduleDomain(dsPolicyId)
      } catch (error) {
        // Rota de domínio de ESCALAS (`schedule`), não de horário.
        console.error('Erro ao buscar domínio de escalas', error)
        throw error
      }
    },

    async promoteAuthorizedDoctor(policyAuthorizationId: number): Promise<void> {
      const service = new DsPolicyService()
      try {
        await service.promoteAuthorizedDoctor(policyAuthorizationId)
      } catch (error) {
        console.error('Erro ao promover médico:', error)
        throw error
      }
    },

    setRecentlyCreatedPolicy(policy: DsPolicyDetails) {
      this.currentPolicy = policy
    },

    async applyFilters(filters: Partial<DsPolicySearchParams>) {
      if (filters.q !== undefined) this.policyQuery.q = filters.q
      if (filters.companyIds !== undefined) this.policyQuery.companyIds = filters.companyIds
      if (filters.isActive !== undefined) this.policyQuery.isActive = filters.isActive
      else delete this.policyQuery.isActive

      this.policyQuery.offset = 0
      await this.fetchDsPolicies()
    },

    clearFilters() {
      this.policyQuery = {
        q: '',
        companyIds: '',
        limit: 10,
        offset: 0
      }
      this.fetchDsPolicies()
    },

    changePage(page: number) {
      this.page = page
      this.policyQuery.offset = (page - 1) * this.policyQuery.limit!
      this.fetchDsPolicies()
    }
  }
})
